import { useMemo, useCallback } from 'react';
import { CommandAction } from '../types';
import { useUI, useSettings, usePreviewContext, useEditorState } from '../contexts';

type UseCommandsParams = {
  handleLint: () => Promise<boolean>;
  handleRun: () => Promise<boolean>;
  handleBuild: () => Promise<boolean>;
  handleClearCache: () => void;
  handleReloadProject: () => void;
  handleExportProject: () => void;
  handleImportProject: () => Promise<void>;
  handleExportToCodeSandbox: () => void;
  handleExportToStackBlitz: () => void;
  handleInitProjectSettings: () => void;
};

export function useCommands({
  handleLint,
  handleRun,
  handleBuild,
  handleClearCache,
  handleReloadProject,
  handleExportProject,
  handleImportProject,
  handleExportToCodeSandbox,
  handleExportToStackBlitz,
  handleInitProjectSettings,
}: UseCommandsParams) {
  // Get UI state from context
  const { commandQuery, setStatus, setTimeMachineOpen, setTimeMachineFilePath, setShortcutsModalOpen, setGistExportModalOpen, setBranchCompareModalOpen, setRemoteManagementModalOpen, setGithubImportModalOpen, setNpmImportModalOpen, setTutorialModalOpen, setShareLinkModalOpen, setEmbedCodeModalOpen, setCustomThemeModalOpen, setPackageManagerModalOpen, setCloneRepoModalOpen, setFeatureWalkthroughModalOpen, setLaunchConfigModalOpen, setVideoTutorialsModalOpen, setThreeWayMergeModalOpen } = useUI();

  // Get editor state from context
  const { activeFile } = useEditorState();

  // Get settings from context
  const { autoSaveEnabled, setAutoSaveEnabled, theme, setTheme, minimapEnabled, setMinimapEnabled, vimEnabled, setVimEnabled, blameEnabled, setBlameEnabled, fontSize, increaseFontSize, decreaseFontSize, resetFontSize, reducedMotion, toggleReducedMotion, highContrast, toggleHighContrast, colorBlindMode, setColorBlindMode } = useSettings();

  // Get preview state from context
  const { setHotReloadEnabled, setPreviewVersion } = usePreviewContext();
  const toggleAutosave = useCallback(() => {
    const next = !autoSaveEnabled;
    setAutoSaveEnabled(next);
    setStatus(next ? 'Autosave on' : 'Autosave off');
  }, [autoSaveEnabled, setAutoSaveEnabled, setStatus]);

  const toggleHotReload = useCallback(() => {
    setHotReloadEnabled((prev) => {
      const next = !prev;
      if (next) {
        setPreviewVersion((v) => v + 1);
      }
      return next;
    });
  }, [setHotReloadEnabled, setPreviewVersion]);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const toggleMinimap = useCallback(() => {
    const next = !minimapEnabled;
    setMinimapEnabled(next);
    setStatus(next ? 'Minimap enabled' : 'Minimap disabled');
  }, [minimapEnabled, setMinimapEnabled, setStatus]);

  const toggleVimMode = useCallback(() => {
    const next = !vimEnabled;
    setVimEnabled(next);
    setStatus(next ? 'Vim mode enabled' : 'Vim mode disabled');
  }, [vimEnabled, setVimEnabled, setStatus]);

  const toggleBlame = useCallback(() => {
    const next = !blameEnabled;
    setBlameEnabled(next);
    setStatus(next ? 'Git blame enabled' : 'Git blame disabled');
  }, [blameEnabled, setBlameEnabled, setStatus]);

  const openTimeMachine = useCallback(() => {
    if (activeFile) {
      setTimeMachineFilePath(activeFile);
      setTimeMachineOpen(true);
    } else {
      setStatus('No file open');
    }
  }, [activeFile, setTimeMachineFilePath, setTimeMachineOpen, setStatus]);

  const openShortcuts = useCallback(() => {
    setShortcutsModalOpen(true);
  }, [setShortcutsModalOpen]);

  const openGistExport = useCallback(() => {
    setGistExportModalOpen(true);
  }, [setGistExportModalOpen]);

  const openBranchCompare = useCallback(() => {
    setBranchCompareModalOpen(true);
  }, [setBranchCompareModalOpen]);

  const openRemoteManagement = useCallback(() => {
    setRemoteManagementModalOpen(true);
  }, [setRemoteManagementModalOpen]);

  const openGithubImport = useCallback(() => {
    setGithubImportModalOpen(true);
  }, [setGithubImportModalOpen]);

  const openNpmImport = useCallback(() => {
    setNpmImportModalOpen(true);
  }, [setNpmImportModalOpen]);

  const openTutorial = useCallback(() => {
    setTutorialModalOpen(true);
  }, [setTutorialModalOpen]);

  const openShareLink = useCallback(() => {
    setShareLinkModalOpen(true);
  }, [setShareLinkModalOpen]);

  const openEmbedCode = useCallback(() => {
    setEmbedCodeModalOpen(true);
  }, [setEmbedCodeModalOpen]);

  const openCustomTheme = useCallback(() => {
    setCustomThemeModalOpen(true);
  }, [setCustomThemeModalOpen]);

  const openPackageManager = useCallback(() => {
    setPackageManagerModalOpen(true);
  }, [setPackageManagerModalOpen]);

  const openCloneRepo = useCallback(() => {
    setCloneRepoModalOpen(true);
  }, [setCloneRepoModalOpen]);

  const openFeatureWalkthroughs = useCallback(() => {
    setFeatureWalkthroughModalOpen(true);
  }, [setFeatureWalkthroughModalOpen]);

  const openLaunchConfig = useCallback(() => {
    setLaunchConfigModalOpen(true);
  }, [setLaunchConfigModalOpen]);

  const openVideoTutorials = useCallback(() => {
    setVideoTutorialsModalOpen(true);
  }, [setVideoTutorialsModalOpen]);

  const openThreeWayMerge = useCallback(() => {
    setThreeWayMergeModalOpen(true);
  }, [setThreeWayMergeModalOpen]);

  const focusSettings = useCallback(() => {
    const el = document.querySelector<HTMLInputElement>('.api-base-input');
    if (el) {
      el.focus();
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setStatus('Settings açıldı');
  }, [setStatus]);

  const commandActions = useMemo<CommandAction[]>(
    () => [
      {
        id: 'lint',
        title: 'Run lint',
        detail: 'ESLint ile statik analiz',
        run: () => void handleLint(),
      },
      {
        id: 'test',
        title: 'Run tests',
        detail: 'Jest/test suite çalıştır',
        run: () => void handleRun(),
      },
      {
        id: 'build',
        title: 'Run build',
        detail: 'Üretim build hazırla',
        run: () => void handleBuild(),
      },
      {
        id: 'toggle-autosave',
        title: 'Toggle autosave',
        detail: 'Otomatik kaydetmeyi aç/kapat',
        run: () => toggleAutosave(),
      },
      {
        id: 'toggle-hot-reload',
        title: 'Toggle hot reload',
        detail: 'Önizleme canlı yenilemeyi aç/kapat',
        run: () => toggleHotReload(),
      },
      {
        id: 'open-settings',
        title: 'Open settings',
        detail: 'API/Project ayarlarına git',
        run: () => focusSettings(),
      },
      {
        id: 'clear-cache',
        title: 'Clear cache',
        detail: 'Yerel cache\'i temizle',
        run: () => handleClearCache(),
      },
      {
        id: 'reload-project',
        title: 'Reload project',
        detail: 'Dosya ağacını yenile',
        run: () => handleReloadProject(),
      },
      {
        id: 'toggle-minimap',
        title: 'Toggle minimap',
        detail: 'Editör minimap\'ini aç/kapat',
        run: () => toggleMinimap(),
      },
      {
        id: 'toggle-vim-mode',
        title: 'Toggle Vim mode',
        detail: 'Vim düzenleme modunu aç/kapat',
        run: () => toggleVimMode(),
      },
      {
        id: 'toggle-git-blame',
        title: 'Toggle Git blame',
        detail: 'Show/hide inline git blame annotations',
        run: () => toggleBlame(),
      },
      {
        id: 'compare-branches',
        title: 'Git: Compare Branches',
        detail: 'Compare changes between two branches',
        run: () => openBranchCompare(),
      },
      {
        id: 'manage-remotes',
        title: 'Git: Manage Remotes',
        detail: 'Add, remove, or view remote repositories',
        run: () => openRemoteManagement(),
      },
      {
        id: 'time-machine',
        title: 'Time Machine: View File History',
        detail: 'View the current file at previous commits',
        run: () => openTimeMachine(),
      },
      {
        id: 'export-project',
        title: 'Export: Download as ZIP',
        detail: 'Download all project files as a ZIP archive',
        run: () => handleExportProject(),
      },
      {
        id: 'import-project',
        title: 'Import: Upload ZIP',
        detail: 'Import project files from a ZIP archive',
        run: () => void handleImportProject(),
      },
      {
        id: 'import-github',
        title: 'Import: From GitHub',
        detail: 'Import project files from a GitHub repository',
        run: () => openGithubImport(),
      },
      {
        id: 'import-npm',
        title: 'Import: From npm',
        detail: 'Import package source files from npm registry',
        run: () => openNpmImport(),
      },
      {
        id: 'export-gist',
        title: 'Export: Create GitHub Gist',
        detail: 'Export project files to GitHub Gist',
        run: () => openGistExport(),
      },
      {
        id: 'export-codesandbox',
        title: 'Export: Open in CodeSandbox',
        detail: 'Open project in CodeSandbox online IDE',
        run: () => handleExportToCodeSandbox(),
      },
      {
        id: 'export-stackblitz',
        title: 'Export: Open in StackBlitz',
        detail: 'Open project in StackBlitz online IDE',
        run: () => handleExportToStackBlitz(),
      },
      {
        id: 'share-link',
        title: 'Share: Generate Link',
        detail: 'Create a shareable URL for the project',
        run: () => openShareLink(),
      },
      {
        id: 'embed-code',
        title: 'Share: Generate Embed Code',
        detail: 'Create embeddable iframe code for the project',
        run: () => openEmbedCode(),
      },
      {
        id: 'custom-themes',
        title: 'Appearance: Custom Themes',
        detail: 'Create and manage custom color themes',
        run: () => openCustomTheme(),
      },
      {
        id: 'keyboard-shortcuts',
        title: 'Show Keyboard Shortcuts',
        detail: 'Display all keyboard shortcuts (Cmd+K Cmd+S)',
        run: () => openShortcuts(),
      },
      {
        id: 'interactive-tutorial',
        title: 'Help: Interactive Tutorial',
        detail: 'Start the interactive tutorial to learn IDE features',
        run: () => openTutorial(),
      },
      {
        id: 'increase-font-size',
        title: 'Editor: Increase Font Size',
        detail: `Increase editor font size (current: ${fontSize}px)`,
        run: () => increaseFontSize(),
      },
      {
        id: 'decrease-font-size',
        title: 'Editor: Decrease Font Size',
        detail: `Decrease editor font size (current: ${fontSize}px)`,
        run: () => decreaseFontSize(),
      },
      {
        id: 'reset-font-size',
        title: 'Editor: Reset Font Size',
        detail: 'Reset editor font size to default (14px)',
        run: () => resetFontSize(),
      },
      {
        id: 'toggle-reduced-motion',
        title: 'Accessibility: Toggle Reduced Motion',
        detail: `${reducedMotion ? 'Enable' : 'Disable'} animations and transitions`,
        run: () => toggleReducedMotion(),
      },
      {
        id: 'toggle-high-contrast',
        title: 'Accessibility: Toggle High Contrast',
        detail: `${highContrast ? 'Disable' : 'Enable'} high contrast mode for better visibility`,
        run: () => toggleHighContrast(),
      },
      {
        id: 'color-blind-none',
        title: 'Accessibility: Color Blind Mode - None',
        detail: `Disable color blind simulation ${colorBlindMode === 'none' ? '(active)' : ''}`,
        run: () => setColorBlindMode('none'),
      },
      {
        id: 'color-blind-protanopia',
        title: 'Accessibility: Color Blind Mode - Protanopia',
        detail: `Red-green color blindness (red-weak) ${colorBlindMode === 'protanopia' ? '(active)' : ''}`,
        run: () => setColorBlindMode('protanopia'),
      },
      {
        id: 'color-blind-deuteranopia',
        title: 'Accessibility: Color Blind Mode - Deuteranopia',
        detail: `Red-green color blindness (green-weak) ${colorBlindMode === 'deuteranopia' ? '(active)' : ''}`,
        run: () => setColorBlindMode('deuteranopia'),
      },
      {
        id: 'color-blind-tritanopia',
        title: 'Accessibility: Color Blind Mode - Tritanopia',
        detail: `Blue-yellow color blindness ${colorBlindMode === 'tritanopia' ? '(active)' : ''}`,
        run: () => setColorBlindMode('tritanopia'),
      },
      {
        id: 'color-blind-achromatopsia',
        title: 'Accessibility: Color Blind Mode - Achromatopsia',
        detail: `Complete color blindness (monochromacy) ${colorBlindMode === 'achromatopsia' ? '(active)' : ''}`,
        run: () => setColorBlindMode('achromatopsia'),
      },
      {
        id: 'package-manager',
        title: 'Package Manager',
        detail: 'Add or remove npm packages from package.json',
        run: () => openPackageManager(),
      },
      {
        id: 'clone-repo',
        title: 'Git: Clone Repository',
        detail: 'Clone a Git repository from GitHub or GitLab',
        run: () => openCloneRepo(),
      },
      {
        id: 'init-project-settings',
        title: 'Project: Initialize Settings',
        detail: 'Create .atlas-ide.json for project-specific settings',
        run: () => handleInitProjectSettings(),
      },
      {
        id: 'feature-walkthroughs',
        title: 'Help: Feature Walkthroughs',
        detail: 'Guided tours of IDE features like Git, Editor, and more',
        run: () => openFeatureWalkthroughs(),
      },
      {
        id: 'launch-configurations',
        title: 'Run: Launch Configurations',
        detail: 'Manage and run project configurations',
        run: () => openLaunchConfig(),
      },
      {
        id: 'video-tutorials',
        title: 'Help: Video Tutorials',
        detail: 'Watch video tutorials to learn IDE features',
        run: () => openVideoTutorials(),
      },
      {
        id: 'three-way-merge',
        title: 'Git: 3-Way Merge Editor',
        detail: 'Resolve merge conflicts with 3-way comparison view',
        run: () => openThreeWayMerge(),
      },
    ],
    [focusSettings, handleBuild, handleClearCache, handleLint, handleReloadProject, handleRun, toggleAutosave, toggleHotReload, toggleMinimap, toggleVimMode, toggleBlame, openTimeMachine, openBranchCompare, openRemoteManagement, openGithubImport, openNpmImport, openTutorial, openShareLink, openEmbedCode, openCustomTheme, openPackageManager, openCloneRepo, openFeatureWalkthroughs, openLaunchConfig, openVideoTutorials, openThreeWayMerge, handleExportProject, handleImportProject, openGistExport, handleExportToCodeSandbox, handleExportToStackBlitz, handleInitProjectSettings, openShortcuts, fontSize, increaseFontSize, decreaseFontSize, resetFontSize, reducedMotion, toggleReducedMotion, highContrast, toggleHighContrast, colorBlindMode, setColorBlindMode]
  );

  const filteredCommands = useMemo(() => {
    const q = commandQuery.trim().toLowerCase();
    if (!q) return commandActions;
    return commandActions.filter(
      (cmd) => cmd.title.toLowerCase().includes(q) || cmd.detail.toLowerCase().includes(q) || cmd.id.includes(q)
    );
  }, [commandActions, commandQuery]);

  return {
    commandActions,
    filteredCommands,
    toggleAutosave,
    toggleHotReload,
    toggleTheme,
    toggleMinimap,
    toggleVimMode,
    toggleBlame,
    focusSettings,
  };
}
